import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { siteInfo } from '@/mocks/site';

export default function AboutPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = '关于我们 - g点网';
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const breadcrumbItems = [
    { label: t('about') },
  ];

  const orgSchema = {
    name: siteInfo.name,
    url: 'https://gpoint.com',
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    foundingDate: siteInfo.founded,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京',
      addressRegion: '海淀区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      email: siteInfo.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('breadcrumbHome'), item: 'https://gpoint.com/' },
      { '@type': 'ListItem', position: 2, name: t('about') },
    ],
  };

  const reputationItems = [
    { icon: 'ri-shield-check-line', title: '安全可信', desc: '通过多项国家级安全认证，采用银行级数据加密技术，全方位保障用户信息与隐私安全。' },
    { icon: 'ri-award-line', title: '行业认可', desc: '连续多年获得行业优秀平台称号，被多家权威媒体评为最受用户信赖的综合服务平台之一。' },
    { icon: 'ri-user-heart-line', title: '用户至上', desc: `累计服务超过${siteInfo.stats.users}用户，用户满意度高达${siteInfo.stats.satisfaction}，持续优化用户体验。` },
    { icon: 'ri-service-line', title: '专业团队', desc: '拥有经验丰富的技术与运营团队，7×24小时在线支持，为用户提供及时、专业的问题解决方案。' },
  ];

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - g点网',
        url: 'https://gpoint.com/about',
        description: `了解g点网品牌故事、平台信誉及联系方式。${siteInfo.description}`,
        inLanguage: 'zh-CN',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {t('aboutTitle')}
              </h1>
              <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {t('aboutSubtitle')}
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6">
                {t('brandIntro')}
              </h2>
              <div className="prose prose-base max-w-none text-foreground-700 leading-relaxed space-y-4">
                <p>
                  {siteInfo.name}成立于{siteInfo.founded}年，是一家专注于为用户提供综合性网络服务的平台。
                  自创立以来，我们始终秉持"用户第一、安全至上"的核心理念，致力于打造一个让用户用得放心、用得舒心的网络服务平台。
                </p>
                <p>
                  经过{siteInfo.stats.years}年的持续发展，{siteInfo.name}已累计服务超过{siteInfo.stats.users}用户，
                  平台内容总数突破{siteInfo.stats.articles}篇，涵盖使用指南、功能介绍、安全防护等多个领域，
                  已成为广大用户日常网络生活中不可或缺的重要组成部分。
                </p>
                <p>
                  我们的团队由来自互联网、安全、用户体验等领域的资深专家组成，凭借专业的技术能力和对用户需求的深刻理解，
                  不断优化平台功能，提升服务质量。无论是新用户还是资深用户，都能在{siteInfo.name}找到适合自己的内容和服务。
                </p>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-10 text-center">
                {t('reputation')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {reputationItems.map((item) => (
                  <div key={item.title} className="text-center p-6 rounded-xl bg-background-50 border border-secondary-100">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-10 text-center">
                {t('contactUs')}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-secondary-100">
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('email')}</h4>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-secondary-100">
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('phone')}</h4>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-secondary-100">
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('address')}</h4>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-secondary-100">
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('workingHours')}</h4>
                      <p className="text-sm text-foreground-600">{siteInfo.workingHours}</p>
                    </div>
                  </div>
                </div>

                <form className="p-6 rounded-xl bg-background-100 border border-secondary-100" data-readdy-form="contact" action="https://readdy.ai/api/form/d9qoi2ue3oq4jqumto10" method="POST">
                  <h4 className="text-base font-semibold text-foreground-900 mb-5">发送消息</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-foreground-600 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full h-10 px-3 text-sm rounded-lg border border-secondary-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground-600 mb-1.5">{t('email')}</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full h-10 px-3 text-sm rounded-lg border border-secondary-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground-600 mb-1.5">消息内容</label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-secondary-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的消息（限500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot"
                    />
                    <button
                      type="submit"
                      className="w-full h-11 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      发送消息
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}